import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../api';
import KpiCard from '../../components/KpiCard';
import { useAsync } from '../../hooks/useAsync';
import type { RetentionContractClassification, RetentionDetailResponse } from '../../types';

function fmtCurrency(v: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);
}

function fmtPct(v: number) {
  return `${(v || 0).toFixed(1)}%`;
}

const CLASSIFICATION_OPTIONS: Array<{ value: 'all' | RetentionContractClassification; label: string }> = [
  { value: 'all', label: 'All classifications' },
  { value: 'ongoing', label: 'Ongoing / scheduled' },
  { value: 'upsell', label: 'Upsell' },
  { value: 'renewal_flat', label: 'Renewal flat' },
  { value: 'downsell', label: 'Downsell' },
  { value: 'churn', label: 'Churn' },
  { value: 'pilot_not_converted', label: 'Pilot not converted' },
];

function labelForClassification(value: RetentionContractClassification) {
  if (value === 'ongoing') return 'Ongoing / scheduled';
  if (value === 'renewal_flat') return 'Renewal flat';
  if (value === 'pilot_not_converted') return 'Pilot not converted';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function RetentionMetricsDetail({ scope }: { scope: 'admin' | 'investor' }) {
  const endpoint = scope === 'admin' ? '/api/admin/kpis/retention-detail' : '/api/investor/kpis/retention-detail';
  const backHref = scope === 'admin' ? '/admin/kpis' : '/app/kpis';
  const { data, loading, error } = useAsync<RetentionDetailResponse>(() => apiGet(endpoint), [endpoint]);

  const [selectedYear, setSelectedYear] = useState<'all' | string>('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [classification, setClassification] = useState<'all' | RetentionContractClassification>('all');

  const filteredContracts = useMemo(() => {
    const companyNeedle = companyFilter.trim().toLowerCase();
    return (data?.contracts || []).filter((contract) => {
      const matchesYear =
        selectedYear === 'all' ||
        String(contract.startDate).slice(0, 4) === selectedYear ||
        contract.revenueByYear.some((item) => String(item.year) === selectedYear);
      const matchesCompany = !companyNeedle || contract.companyName.toLowerCase().includes(companyNeedle);
      const matchesClassification = classification === 'all' || contract.classification === classification;
      return matchesYear && matchesCompany && matchesClassification;
    });
  }, [classification, companyFilter, data?.contracts, selectedYear]);

  if (loading) return <div className="card">Loading retention detail…</div>;
  if (error || !data) return <div className="card">Couldn't load retention detail. ({error || 'error'})</div>;

  const latest = data.summaryByYear.find((row) => row.year === data.latestYear) || data.summaryByYear[data.summaryByYear.length - 1] || null;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>Retention Breakdown</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Updated: {new Date(data.computedAt).toLocaleString()}
            </div>
          </div>
          <Link className="btn" to={backHref}>
            Back to dashboard
          </Link>
        </div>
      </div>

      {latest ? (
        <div className="grid kpis">
          <KpiCard title="NRR" value={latest.nrr} format="percentage" />
          <KpiCard title="GRR" value={latest.grr} format="percentage" />
          <KpiCard title="Logo Churn" value={latest.logoChurnRate} format="percentage" />
          <KpiCard title="Pilot Conversion" value={latest.pilotConversionRate} format="percentage" />
        </div>
      ) : null}

      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 8 }}>How the metric is built</div>
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          NRR and GRR use the cohort of customers with revenue in the previous year. New logos do not affect the denominator. Pilots with term up to 3 months never count as churn, and a customer that returns in any future year is not counted as churn.
        </div>
      </div>

      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Cohort breakdown by year</div>
        {data.summaryByYear.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th>Base Revenue</th>
                  <th>Retained</th>
                  <th>Expansion</th>
                  <th>Contraction</th>
                  <th>NRR</th>
                  <th>GRR</th>
                  <th>Logo Churn</th>
                  <th>Pilot Conversion</th>
                </tr>
              </thead>
              <tbody>
                {data.summaryByYear.map((row) => (
                  <tr key={row.year}>
                    <td>{row.baseYear} → {row.year}</td>
                    <td>{fmtCurrency(row.baseRevenue)}</td>
                    <td>{fmtCurrency(row.retainedRevenue)}</td>
                    <td>{fmtCurrency(row.expansionRevenue)}</td>
                    <td>{fmtCurrency(row.contractionRevenue)}</td>
                    <td>{fmtPct(row.nrr)}</td>
                    <td>{fmtPct(row.grr)}</td>
                    <td>{fmtPct(row.logoChurnRate)}</td>
                    <td>{fmtPct(row.pilotConversionRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="muted">No retention cohorts available yet.</div>
        )}
      </div>

      {data.dataQuality.excludedDeals ? (
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 8 }}>Data quality</div>
          <div className="muted" style={{ lineHeight: 1.6 }}>
            {data.dataQuality.excludedDeals} won deals were excluded from retention because contract data is incomplete.
          </div>
          <div style={{ height: 10 }} />
          <div className="grid kpis">
            <KpiCard title="Missing company" value={data.dataQuality.missingCompany} format="number" />
            <KpiCard title="Missing start date" value={data.dataQuality.missingStartDate} format="number" />
            <KpiCard title="Missing term" value={data.dataQuality.missingContractTermMonths} format="number" />
          </div>
        </div>
      ) : null}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Contract detail</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {filteredContracts.length} contracts shown
            </div>
          </div>
        </div>
        <div style={{ height: 12 }} />
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Year</div>
            <select className="select" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
              <option value="all">All years</option>
              {data.summaryByYear.map((row) => (
                <option key={row.year} value={String(row.year)}>
                  {row.year}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Company</div>
            <input className="input" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} placeholder="Search company" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Classification</div>
            <select className="select" value={classification} onChange={(e) => setClassification(e.target.value as any)}>
              {CLASSIFICATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ height: 12 }} />
        {filteredContracts.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Deal</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Amount</th>
                  <th>ACV</th>
                  <th>Classification</th>
                  <th>Next contract</th>
                  <th>Revenue by year</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.map((contract) => (
                  <tr key={contract.dealId}>
                    <td>{contract.companyName}</td>
                    <td>
                      <div>{contract.dealName}</div>
                      <div className="muted" style={{ fontSize: 12 }}>#{contract.dealId}</div>
                    </td>
                    <td>{contract.startDate}</td>
                    <td>{contract.endDate}</td>
                    <td>{fmtCurrency(contract.amount)}</td>
                    <td>{fmtCurrency(contract.acv)}</td>
                    <td>{labelForClassification(contract.classification)}</td>
                    <td>{contract.nextStartDate || 'No follow-up'}</td>
                    <td>
                      {contract.revenueByYear.map((item) => `${item.year}: ${fmtCurrency(item.revenue)}`).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="muted">No contracts match the current filters.</div>
        )}
      </div>
    </div>
  );
}
